import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { toast } from 'sonner';
import { Users, Loader2 } from 'lucide-react';

import { UserCard } from '@/components/admin/users/UserCard';
import { UserFiltersBar } from '@/components/admin/users/UserFiltersBar';
import { UserDeleteDialog } from '@/components/admin/users/UserDeleteDialog';

import { API } from '@/lib/apiBase';
const ROLE_RANK = { admin: 0, teacher: 1, parent: 2 };

// Pure sort function — extracted so it's easy to test and doesn't allocate
// a new comparator on every render.
const sortUsers = (list, sortBy) => {
  const arr = [...list];
  switch (sortBy) {
    case 'oldest':
      return arr.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    case 'name_az':
      return arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    case 'name_za':
      return arr.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    case 'email':
      return arr.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
    case 'role':
      return arr.sort((a, b) => (ROLE_RANK[a.role] ?? 99) - (ROLE_RANK[b.role] ?? 99));
    case 'newest':
    default:
      return arr.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }
};

export default function AdminUsers() {
  const { getAuthHeaders, user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/users`, {
        headers: getAuthHeaders(), credentials: 'include',
      });
      if (response.ok) setUsers(await response.json());
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, t]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleRoleChange = useCallback(async (userId, newRole) => {
    if (userId === currentUser?.user_id) {
      toast.error(t('users.cannotChangeOwnRole'));
      return;
    }
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole }),
      });
      if (!response.ok) throw new Error();
      toast.success(t('common.success'));
      fetchUsers();
    } catch (error) {
      toast.error(t('common.error'));
    }
  }, [currentUser, getAuthHeaders, t, fetchUsers]);

  const handleDelete = useCallback(async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(), credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed');
      }
      toast.success(t('common.success'));
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.message || t('common.error'));
    }
  }, [getAuthHeaders, t, fetchUsers]);

  const filteredSortedUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = users.filter((u) => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (!q) return true;
      return (
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    });
    return sortUsers(filtered, sortBy);
  }, [users, search, roleFilter, sortBy]);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12" data-testid="admin-users">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t('users.title')}</h1>
        <p className="text-muted-foreground">{t('users.description')}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">{t('users.noUsers')}</h3>
        </div>
      ) : (
        <>
          <UserFiltersBar
            search={search} setSearch={setSearch}
            roleFilter={roleFilter} setRoleFilter={setRoleFilter}
            sortBy={sortBy} setSortBy={setSortBy}
            resultsCount={filteredSortedUsers.length}
            totalCount={users.length}
          />

          {filteredSortedUsers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {t('users.noUsers')}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredSortedUsers.map((u) => (
                <UserCard
                  key={u.user_id}
                  user={u}
                  isCurrentUser={u.user_id === currentUser?.user_id}
                  onRoleChange={handleRoleChange}
                  onDeleteRequest={setDeleteConfirm}
                />
              ))}
            </div>
          )}
        </>
      )}

      <UserDeleteDialog
        user={deleteConfirm}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}
